<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

type Theme = 'dark' | 'light';

const STORAGE_KEY = 'tm-theme';
const theme = ref<Theme>('dark');

/**
 * Apply data-theme=dark|light on <html> so the CSS token blocks
 * (`:root[data-theme='light'] { … }`) switch the palette.
 */
function apply(next: Theme): void {
  if (typeof document === 'undefined') return;
  if (next === 'light') document.documentElement.setAttribute('data-theme', 'light');
  else document.documentElement.removeAttribute('data-theme');
}

onMounted(() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      theme.value = stored;
    }
    apply(theme.value);
  } catch {
    // localStorage may be unavailable (private mode) — silent fallback to dark
  }
});

watch(theme, (next) => {
  apply(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* noop */
  }
});

const isDark = computed(() => theme.value === 'dark');
const title = computed(() =>
  theme.value === 'light' ? 'Перейти в тёмную тему' : 'Перейти в светлую тему',
);

function toggle(): void {
  theme.value = theme.value === 'light' ? 'dark' : 'light';
}
</script>

<template>
  <button
    type="button"
    class="btn btn--ghost btn--small header-search-btn theme-toggle"
    :aria-label="title"
    :title="title"
    @click="toggle"
  >
    <!-- Sun — shown in dark mode (click to go light) -->
    <svg
      v-if="isDark"
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="5"
      />
      <line
        x1="12"
        y1="1"
        x2="12"
        y2="3"
      />
      <line
        x1="12"
        y1="21"
        x2="12"
        y2="23"
      />
      <line
        x1="4.22"
        y1="4.22"
        x2="5.64"
        y2="5.64"
      />
      <line
        x1="18.36"
        y1="18.36"
        x2="19.78"
        y2="19.78"
      />
      <line
        x1="1"
        y1="12"
        x2="3"
        y2="12"
      />
      <line
        x1="21"
        y1="12"
        x2="23"
        y2="12"
      />
      <line
        x1="4.22"
        y1="19.78"
        x2="5.64"
        y2="18.36"
      />
      <line
        x1="18.36"
        y1="5.64"
        x2="19.78"
        y2="4.22"
      />
    </svg>
    <!-- Moon — shown in light mode (click to go dark) -->
    <svg
      v-else
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  </button>
</template>
